import React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel, { stepLabelClasses } from '@mui/material/StepLabel';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ExclamationDiamond, Check2 } from 'react-bootstrap-icons';
import { styled, useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import {
	newDate,
	newTimeTz,
	isValidDateTime,
	isValidDate,
	newTimeUTCAddTz,
} from '../../../utils/formatters';

const useStepIconStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: '#787574',
		display: 'flex',
		width: '10rem',
		height: '4px',
		alignItems: 'center',
	},
	disableLine: {
		height: '4px',
		backgroundColor: '#787574',
		width: '10rem',
	},
	active: {
		height: '4px',
		backgroundColor: '#8BC34A',
		width: '10rem',
		maxWidth: '100%',
		zIndex: 1,
	},
	completed: {
		height: '4px',
		backgroundColor: '#8BC34A',
		width: '10rem',
		maxWidth: '100%',
		zIndex: 1,
	},
	error: {
		height: '4px',
		backgroundColor: '#BB0000',
		width: '10rem',
		maxWidth: '100%',
	},
}));

function getIcon({ error, completed }, classes) {
	if (error) {
		return (
			<hr
				className={classes.error}
				aria-label="Error"
				tabIndex={-1}
				role="presentation"
			/>
		);
	} else if (completed) {
		return (
			<hr
				className={classes.completed}
				aria-label="Completed"
				tabIndex={-1}
				role="presentation"
			/>
		);
	}
	return (
		<hr
			className={classes.disableLine}
			aria-label="Disable"
			tabIndex={-1}
			role="presentation"
		/>
	);
}

function StepIcon({ error, completed, active }) {
	const classes = useStepIconStyles();
	const theme = useTheme(); // Add this line
	return (
		<div
			className={clsx(classes.root, {
				[classes.active]: active,
			})}
		>
			{getIcon({ error, completed }, classes, theme)}
		</div>
	);
}

const useStyles = makeStyles(() => ({
	root: {
		marginLeft: '-.5rem',
		width: '100%',
		'& .MuiStepConnector-lineHorizontal': {
			display: 'none',
		},
	},
}));

const ColorlibStepLabel = styled(StepLabel)(() => ({
	[`& .${stepLabelClasses.label}`]: {
		[`&.${stepLabelClasses.completed}`]: {
			color: '#8BC34A',
			content: '"e876"',
			fontWeight: 'normal',
		},
		[`&.${stepLabelClasses.active}`]: {
			color: '#8BC34A',
			fontWeight: 'normal',
		},
		[`&.${stepLabelClasses.error}`]: {
			color: '#BB0000',
			fontWeight: 'normal',
		},

		color: '#787574',
		marginLeft: '1rem',
		marginTop: '1.25rem',
	},
}));
function OrderDetailStepper({
	error,
	cancelled,
	shippingCancelledStatus,
	alert,
	activeStep,
	stepOrdered,
	stepInProcess,
	stepShipped,
	stepDelivered,
	stepInvoiced,
	dateOrdered,
	dateInProcess,
	dateShipped,
	dateDelivered,
	dateInvoiced,
	reachedOrdered,
	reachedInProcess,
	reachedShipped,
	reachedDelivered,
	reachedInvoiced,
	brazilStatus,
}) {
	const classes = useStyles();
	const isError = error;
	const isShippingCancelled = shippingCancelledStatus;
	const isCancelled = cancelled;
	const isAlert = alert;
	const { t } = useTranslation('order_statuses');
	const theme = useTheme();

	const statusAlertCancel = () => {
		if (isAlert) {
			return t('Alert');
		} else if (isCancelled) {
			return t('Cancelled');
		} else if (isShippingCancelled) {
			return t('Cancelled');
		}
		return '';
	};

	const getTime = (index) => {
		if (isValidDateTime(dates[index]) && index === 3 && dates[index] !== '') {
			return newTimeUTCAddTz(dates[index]);
		} else if (dates[index]) {
			return `${newTimeTz(dates[index])}`;
		}
		return '';
	};

	const steps = [
		stepOrdered,
		stepInProcess,
		stepShipped,
		stepDelivered,
		stepInvoiced,
	];
	const dates = [
		dateOrdered,
		dateInProcess,
		dateShipped,
		dateDelivered,
		dateInvoiced,
	];
	const reachedSteps = [
		reachedOrdered,
		reachedInProcess,
		reachedShipped,
		reachedDelivered,
		reachedInvoiced,
	];

	const isStepFailed = (step) => step === 1;

	return (
		<Box sx={{ width: '100%' }} className={classes.root}>
			<Stepper
				activeStep={dateDelivered && activeStep === 3 ? 4 : activeStep}
				alternativeLabel
				aria-label="active step"
				role="paragraph"
			>
				{steps.map((label, index) => {
					const labelProps = {};
					const hasReachedStep =
						typeof reachedSteps[index] === 'boolean'
							? reachedSteps[index]
							: Boolean(dates[index]);
					if (
						isStepFailed(index) &&
						(isAlert || isError || isShippingCancelled)
					) {
						labelProps.error = true;
					}

					return (
						<Step key={label} active={index === activeStep - 1}>
							<ColorlibStepLabel
								error={
									isStepFailed(index) &&
									(isAlert || isError || isShippingCancelled)
								}
								className="stepLabel"
								StepIconComponent={StepIcon}
								aria-label={t('shipment')}
								role="paragraph"
							>
								<Grid
									container
									spacing={2}
									style={{
										display: 'flex',
										alignItems: 'left',
										justifyContent: 'flex-start',
									}}
									aria-label={t('shipment')}
								>
									{hasReachedStep &&
									!(
										isStepFailed(index) &&
										(isAlert || isError || isShippingCancelled)
									) ? (
										<Check2
											style={{
												fontSize: theme.palette.iconSize.Xl,
												marginTop: '.02rem',
												marginRight: '.15rem',
											}}
											aria-label={t('shipment')}
											role="graphics-document"
											aria-hidden="true"
										/>
									) : (
										''
									)}

									{isStepFailed(index) &&
									(isAlert || isError || isShippingCancelled) ? (
										<ExclamationDiamond
											style={{
												fontSize: theme.palette.textSize.sizeSm,
												marginTop: '.18rem',
												marginBottom: '-.18rem',
												marginRight: '.36rem',
											}}
											aria-label="alert"
											role="graphics-document"
											aria-hidden="true"
										/>
									) : (
										''
									)}

									<Typography
										{...labelProps}
										role="paragraph"
										aria-label={t('shipment')}
										style={{
											fontSize: theme.palette.textSize.sizeSm,
											maxWidth: '6rem',
											wordWrap: 'break-word',
											textAlign: 'left',
										}}
									>
										{isStepFailed(index) &&
										(isAlert || isError || isShippingCancelled)
											? `${statusAlertCancel()}`
											: t(`${label}`)}
									</Typography>

									{activeStep === 2 && index === 1 && brazilStatus ? (
										<Grid
											item
											xs={12}
											role="paragraph"
											aria-label={t('shipment')}
											style={{
												textAlign: 'left',
												marginTop: '-1rem',
												marginLeft: '-1rem',
												marginBottom: '.9rem',
												lineHeight: '1rem',
											}}
										>
											<Typography
												variant="caption"
												color="textSecondary"
												role="paragraph"
												aria-label="date"
											>
												{brazilStatus}
											</Typography>
										</Grid>
									) : (
										''
									)}

									<Grid
										item
										xs={12}
										role="paragraph"
										aria-label={t('shipment')}
										style={{
											textAlign: 'left',
											marginTop: '-1.5rem',
											marginLeft: '-1rem',
										}}
									>
										<Typography
											variant="caption"
											color="textSecondary"
											role="paragraph"
											aria-label="date"
										>
											{dates[index] ? `${newDate(dates[index])}` : ''}
										</Typography>
									</Grid>
									<Grid
										item
										xs={12}
										style={{
											textAlign: 'left',
											marginTop: '-1.25rem',
											marginLeft: '-1rem',
										}}
										role="paragraph"
										aria-label={t('shipment')}
									>
										<Typography
											variant="caption"
											color="textSecondary"
											role="paragraph"
											aria-label="time"
										>
											{dates[index] === null || isValidDate(dates[index])
												? ''
												: getTime(index)}
										</Typography>
									</Grid>
								</Grid>
							</ColorlibStepLabel>
						</Step>
					);
				})}
			</Stepper>
		</Box>
	);
}

OrderDetailStepper.propTypes = {
	error: PropTypes.bool,
	cancelled: PropTypes.bool,
	shippingCancelledStatus: PropTypes.bool,
	alert: PropTypes.bool,
	activeStep: PropTypes.number.isRequired,
	stepOrdered: PropTypes.string,
	stepInProcess: PropTypes.string,
	stepShipped: PropTypes.string,
	stepDelivered: PropTypes.string,
	stepInvoiced: PropTypes.string,
	reachedOrdered: PropTypes.bool,
	reachedInProcess: PropTypes.bool,
	reachedShipped: PropTypes.bool,
	reachedDelivered: PropTypes.bool,
	reachedInvoiced: PropTypes.bool,
	brazilStatus: PropTypes.string,
	dateOrdered: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.instanceOf(Date),
	]),
	dateInProcess: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.instanceOf(Date),
	]),
	dateShipped: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.instanceOf(Date),
	]),
	dateDelivered: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.instanceOf(Date),
	]),
	dateInvoiced: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.instanceOf(Date),
	]),
	bolNumber: PropTypes.string,
	orderNumber: PropTypes.string,
};
getIcon.propTypes = {
	error: PropTypes.bool,
	cancelled: PropTypes.bool,
	alert: PropTypes.bool,
	completed: PropTypes.bool,
};

StepIcon.propTypes = {
	active: PropTypes.bool,
	completed: PropTypes.bool,
	error: PropTypes.bool,
};

export default OrderDetailStepper;
